import { Controller } from "@hotwired/stimulus"

// Scroll story: pans/zooms a tall photo toward the focus point of whichever
// step is crossing the middle of the viewport, then reveals that step's hotspot.
export default class extends Controller {
  static targets = ["stage", "frame", "pan", "step", "hotspot", "pip", "fog", "caption", "capEyebrow", "capTitle", "capText"]

  connect() {
    this.index = -1
    this.desktop = window.matchMedia("(min-width: 1024px)")
    this.reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    this.measureNav()
    this.onResize = () => { this.measureNav(); this.apply(this.index, { animate: false }) }
    window.addEventListener("resize", this.onResize)

    // A step is "active" while it spans a thin band in the lower middle of the screen (where its card is about to be read)
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) this.apply(this.stepTargets.indexOf(entry.target), { animate: true })
      })
    }, { rootMargin: "-62% 0px -33% 0px" })
    this.stepTargets.forEach((step) => this.observer.observe(step))

    this.apply(0, { animate: false })
  }

  disconnect() {
    window.removeEventListener("resize", this.onResize)
    this.observer?.disconnect()
    clearTimeout(this.timer)
  }

  measureNav() {
    const header = document.querySelector("header")
    this.element.style.setProperty("--nav-h", `${header ? header.offsetHeight : 0}px`)
  }

  apply(i, { animate }) {
    if (i < 0) return
    const changed = i !== this.index
    this.index = i
    const step = this.stepTargets[i]

    const W = this.frameTarget.clientWidth
    const H = this.frameTarget.clientHeight
    const IW = W
    const img = this.panTarget.querySelector("img")
    const IH = W * img.getAttribute("height") / img.getAttribute("width") // photo height at full frame width
    // Space the camera must leave free: label chips above, and (on phones) the step card below
    const top = 40
    const avail = H - top - (this.desktop.matches ? 40 : 250)
    const focusY = top + avail / 2

    // A step can highlight several items at once (data-hotspot="2 3"); the camera frames all of them
    const hotspots = this.hotspotsFor(step)
    const maxZoom = parseFloat(step.dataset.zoom)
    let z, fx, fy
    if (hotspots.length) {
      const boxes = hotspots.map((h) => ({ x: +h.dataset.x, y: +h.dataset.y, w: +h.dataset.w, h: +h.dataset.h }))
      const x0 = Math.min(...boxes.map((b) => b.x)), x1 = Math.max(...boxes.map((b) => b.x + b.w))
      const y0 = Math.min(...boxes.map((b) => b.y)), y1 = Math.max(...boxes.map((b) => b.y + b.h))
      const margin = Math.max(24, W * 0.04) // gap between the selection and the screen edge
      const fit = Math.min((W - 2 * margin) / ((x1 - x0) / 100 * IW), (avail - 2 * margin) / ((y1 - y0) / 100 * IH))
      z = Math.min(maxZoom, fit)
      fx = (x0 + x1) / 200
      fy = (y0 + y1) / 200
    } else {
      z = maxZoom
      fx = parseFloat(step.dataset.fx) / 100
      fy = parseFloat(step.dataset.fy) / 100
    }
    // Never zoom out past the photo: it always fills the frame, so we only work with what's visible
    z = Math.max(z, 1, H / IH)
    const tx = Math.min(0, Math.max(W - z * IW, W / 2 - z * fx * IW))
    const ty = Math.min(0, Math.max(H - z * IH, focusY - z * fy * IH))

    const pan = this.panTarget
    pan.style.transition = animate && !this.reduced ? "" : "none"
    pan.style.transform = `translate(${tx}px, ${ty}px) scale(${z})`
    this.view = { tx, ty, z, IW, IH }

    this.hotspotTargets.forEach((h) => h.classList.remove("is-active"))
    this.fogTarget.classList.remove("is-active")
    this.pipTargets.forEach((p, n) => p.classList.toggle("is-active", n === i))

    // On desktop the active step's copy shows in the pinned left panel
    const card = step.querySelector(".story__card")
    this.captionTarget.classList.toggle("is-empty", i === 0)
    if (i > 0) {
      this.capEyebrowTarget.textContent = card.querySelector(".story__eyebrow").textContent
      this.capTitleTarget.textContent = card.querySelector(".story__title").textContent
      this.capTextTarget.textContent = card.querySelector(".story__text").textContent
    }

    clearTimeout(this.timer)
    if (hotspots.length) {
      const show = () => {
        hotspots.forEach((h) => { this.place(h); h.classList.add("is-active") })
        this.fog(hotspots)
      }
      if (animate && changed && !this.reduced) this.timer = setTimeout(show, 750)
      else show()
    }
  }

  hotspotsFor(step) {
    return (step.dataset.hotspot || "").split(" ").filter(Boolean).map((n) => this.hotspotTargets[n])
  }

  // Faint haze over everything except the highlighted boxes (evenodd path = frame minus boxes)
  fog(hotspots) {
    const W = this.frameTarget.clientWidth
    const H = this.frameTarget.clientHeight
    const r = 14
    let d = `M0 0H${W}V${H}H0Z`
    hotspots.forEach((h) => {
      const x = parseFloat(h.style.left), y = parseFloat(h.style.top)
      const w = parseFloat(h.style.width), ht = parseFloat(h.style.height)
      d += `M${x + r} ${y}H${x + w - r}A${r} ${r} 0 0 1 ${x + w} ${y + r}V${y + ht - r}A${r} ${r} 0 0 1 ${x + w - r} ${y + ht}` +
           `H${x + r}A${r} ${r} 0 0 1 ${x} ${y + ht - r}V${y + r}A${r} ${r} 0 0 1 ${x + r} ${y}Z`
    })
    this.fogTarget.style.clipPath = `path(evenodd, "${d}")`
    this.fogTarget.classList.add("is-active")
  }

  // Convert the hotspot's photo-relative % box into frame pixels for the current camera
  place(hotspot) {
    const { tx, ty, z, IW, IH } = this.view
    const { x, y, w, h } = hotspot.dataset
    Object.assign(hotspot.style, {
      left: `${tx + z * IW * x / 100}px`,
      top: `${ty + z * IH * y / 100}px`,
      width: `${z * IW * w / 100}px`,
      height: `${z * IH * h / 100}px`
    })
  }
}
